import { FLOATING_POINT_ERROR, isCloseTo } from '../utils/numbers';
import { type Point, point } from './point';

/**
 * Straight line defined by equation y = ax + b
 *
 * However vertical lines cannot be described with this equation
 */
export interface NonVerticalLine {
  readonly a: number;
  readonly b: number;
}

export interface VerticalLine {
  readonly x: number;
}

export type Line = NonVerticalLine | VerticalLine;

export function createVertical(x: number): VerticalLine {
  return { x };
}

export function createNonVertical(a: number, b: number): NonVerticalLine {
  return { a, b };
}

export const fromTwoPoints = (a: Point, b: Point): Line => {
  if (point.equals(a, b)) {
    throw new Error(
      'Two equal points passed, cannot create line from single point',
    );
  }

  if (a.x === b.x) {
    return createVertical(a.x);
  }

  const lineA = (b.y - a.y) / (b.x - a.x);

  return createNonVertical(lineA, a.y - lineA * a.x);
};

export function isVertical(l: Line): l is VerticalLine {
  return Object.hasOwnProperty.call(l, 'x');
}

export function isNonVertical(l: Line): l is NonVerticalLine {
  return Object.hasOwnProperty.call(l, 'a');
}

/**
 * Find perpendicular line intersecting given point
 */
export function perp(l: Line, p: Point): Line {
  if (isVertical(l)) {
    return {
      a: 0,
      b: p.y,
    };
  }
  if (l.a === 0) {
    return { x: p.x };
  }
  const a = -1 / l.a;
  return {
    a,
    b: p.y - a * p.x,
  };
}

function intersectionPointWithVertical(
  l1: NonVerticalLine,
  l2: VerticalLine,
): Point {
  return {
    x: l2.x,
    y: l1.a * l2.x + l1.b,
  };
}

export function intersectionPoint(l1: Line, l2: Line): Point | null {
  if (isVertical(l1) && isVertical(l2)) {
    // parallel lines
    return null;
  }

  if (isVertical(l2)) {
    return intersectionPointWithVertical(l1 as NonVerticalLine, l2); // we know l1 is not vertical from condition above
  }
  if (isVertical(l1)) {
    return intersectionPointWithVertical(l2, l1); // we know l2 is not vertical from condition above
  }

  if (l2.a === l1.a) {
    // parallel lines
    return null;
  }
  const x = (l1.b - l2.b) / (l2.a - l1.a);
  return {
    x,
    y: l1.a * x + l1.b,
  };
}

export const containsPoint = (
  l: Line,
  p: Point,
  tolerance = FLOATING_POINT_ERROR,
): boolean =>
  isVertical(l)
    ? isCloseTo(p.x, l.x, tolerance)
    : isCloseTo(p.y, l.a * p.x + l.b, tolerance);

/**
 * Find closest point on a line from a given point
 *
 * Inspired by https://www.varsitytutors.com/act_math-help/how-to-find-the-equation-of-a-perpendicular-line
 */
export function findClosestPoint(l: Line, p: Point): Point {
  return intersectionPoint(l, perp(l, p))!; // can safely use ! since perpendicular line will never be parallel
}

export const getPointByX =
  (ln: NonVerticalLine) =>
  (x: number): Point => ({
    x,
    y: ln.a * x + ln.b,
  });

export const line = {
  createNonVertical,
  createVertical,
  fromTwoPoints,

  isNonVertical,
  isVertical,

  containsPoint,
  findClosestPoint,
  intersectionPoint,
  perp,
  getPointByX,
};
